import { useEffect, useState, useCallback } from 'react'
import { useLazyQuery, gql, useReactiveVar } from '@apollo/client'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Disclosure } from '@headlessui/react'
import clsx from 'clsx'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify'
import isEqual from 'lodash.isequal'

import {
  KnowledgeCheck,
  DialogReorderAgendaItems,
  DialogEditAgendaModuleItem,
  ConfirmDialog,
} from '../components'

import {
  apolloAuthData,
  apolloEditStatusData,
  getEditStatus,
  setChangesEditStatus,
  setEditStatus,
} from '../services/apollo'
import { ReactComponent as LockIcon } from '../assets/icons/lock-icon.svg'
import { ReactComponent as ChevronRightIcon } from '../assets/icons/chevron-right-icon.svg'
import { ReactComponent as LinkIcon } from '../assets/icons/link-sharp-icon.svg'
import { ROLE_MANAGER, ROLE_FACILITATOR, ROLE_PARTICIPANT } from '../constants'
import { applyUpdatedData, serializeFromQuery, removeTypeName } from '../utils'

const GET_AGENDA_ITEMS_PREPARATION = gql`
  query GetAgendaItems {
    agendaItems(where: { postId: "preparation" }, first: 200) {
      edges {
        node {
          databaseId
          id
          order
          postId
          title
          lock
          detail {
            modules {
              id
              title
              overview
              materialsTitle
              materials {
                id
                name
                url
              }
            }
            knowledgeCheck {
              lock
              questions {
                id
                statement
                choices {
                  id
                  correct
                  text
                }
                feedback {
                  correct
                  incorrect
                }
                point
                gradingMethod
              }
            }
          }
        }
      }
    }
  }
`
const GET_AGENDA_ITEMS_DAY1 = gql`
  query GetAgendaItems {
    agendaItems(where: { postId: "day1" }, first: 200) {
      edges {
        node {
          databaseId
          id
          order
          postId
          title
          lock
          detail {
            modules {
              id
              title
              overview
              materialsTitle
              materials {
                id
                name
                url
              }
            }
            knowledgeCheck {
              lock
              questions {
                id
                statement
                choices {
                  id
                  correct
                  text
                }
                feedback {
                  correct
                  incorrect
                }
                point
                gradingMethod
              }
            }
          }
        }
      }
    }
  }
`
const GET_AGENDA_ITEMS_DAY2 = gql`
  query GetAgendaItems {
    agendaItems(where: { postId: "day2" }) {
      edges {
        node {
          databaseId
          id
          order
          postId
          title
          lock
          detail {
            modules {
              id
              title
              overview
              materialsTitle
              materials {
                id
                name
                url
              }
            }
            knowledgeCheck {
              lock
              questions {
                id
                statement
                choices {
                  id
                  correct
                  text
                }
                feedback {
                  correct
                  incorrect
                }
                point
                gradingMethod
              }
            }
          }
        }
      }
    }
  }
`
const GET_AGENDA_ITEMS_DAY3 = gql`
  query GetAgendaItems {
    agendaItems(where: { postId: "day3" }) {
      edges {
        node {
          databaseId
          id
          order
          postId
          title
          lock
          detail {
            modules {
              id
              title
              overview
              materialsTitle
              materials {
                id
                name
                url
              }
            }
            knowledgeCheck {
              lock
              questions {
                id
                statement
                choices {
                  id
                  correct
                  text
                }
                feedback {
                  correct
                  incorrect
                }
                point
                gradingMethod
              }
            }
          }
        }
      }
    }
  }
`
const GET_AGENDA_ITEMS_DAY4 = gql`
  query GetAgendaItems {
    agendaItems(where: { postId: "day4" }) {
      edges {
        node {
          databaseId
          id
          order
          postId
          title
          lock
          detail {
            modules {
              id
              title
              overview
              materialsTitle
              materials {
                id
                name
                url
              }
            }
            knowledgeCheck {
              lock
              questions {
                id
                statement
                choices {
                  id
                  correct
                  text
                }
                feedback {
                  correct
                  incorrect
                }
                point
                gradingMethod
              }
            }
          }
        }
      }
    }
  }
`

const AgendaDetail = () => {
  let navigate = useNavigate()
  let location = useLocation()
  const page = location.pathname.split('/').pop()
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  const isFacilitator = auth.user?.capabilities?.includes(ROLE_FACILITATOR)
  const isParticipant = auth.user?.capabilities?.includes(ROLE_PARTICIPANT)
  const [updatedModules, setUpdatedModules] = useState([])
  const [removingItemId, setRemovingItemId] = useState('')
  let [searchParams, setSearchParams] = useSearchParams()
  const isEditOn = isManager && !!searchParams.get('edit')
  const editStatus = useReactiveVar(apolloEditStatusData)
  const [isDataLoading, setIsDataLoading] = useState(false)

  const [getAgendaItemsPreparation] = useLazyQuery(
    GET_AGENDA_ITEMS_PREPARATION,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const [getAgendaItemsDay1] = useLazyQuery(GET_AGENDA_ITEMS_DAY1, {
    fetchPolicy: 'no-cache',
  })
  const [getAgendaItemsDay2] = useLazyQuery(GET_AGENDA_ITEMS_DAY2, {
    fetchPolicy: 'no-cache',
  })
  const [getAgendaItemsDay3] = useLazyQuery(GET_AGENDA_ITEMS_DAY3, {
    fetchPolicy: 'no-cache',
  })
  const [getAgendaItemsDay4] = useLazyQuery(GET_AGENDA_ITEMS_DAY4, {
    fetchPolicy: 'no-cache',
  })
  const [agendaItems, setAgendaItems] = useState([])
  const agendaItem = agendaItems[0] ?? null
  const moduleLock =
    isManager || isFacilitator
      ? false
      : isParticipant
      ? !!agendaItem?.lock
      : true
  const kcLock =
    isManager || isFacilitator
      ? false
      : isParticipant
      ? !!agendaItem?.detail?.knowledgeCheck?.lock
      : true
  const modules =
    updatedModules && updatedModules.length > 0 && editStatus.changes.length > 0
      ? updatedModules
      : removeTypeName(agendaItem?.detail?.modules)

  const loadData = useCallback(async () => {
    const query =
      page === 'preparation'
        ? getAgendaItemsPreparation
        : page === 'day1'
        ? getAgendaItemsDay1
        : page === 'day2'
        ? getAgendaItemsDay2
        : page === 'day3'
        ? getAgendaItemsDay3
        : page === 'day4'
        ? getAgendaItemsDay4
        : null

    if (query) {
      setIsDataLoading(true)
      query()
        .then((response) => {
          const _agendaItems =
            response?.data?.agendaItems?.edges?.map((edge) => edge.node) ?? []
          const _agendaItem = _agendaItems[0] ?? null
          const lock =
            isManager || isFacilitator
              ? false
              : isParticipant
              ? !!_agendaItem?.lock
              : true

          if (!_agendaItem || lock) {
            navigate('/agenda', { replace: true })
          }

          // Save original data if in edit mode
          if (isEditOn) {
            setChangesEditStatus({
              ...getEditStatus(),
              originalData: {
                ...getEditStatus().originalData,
                modules: _agendaItem.detail?.modules,
                questions: _agendaItem.detail?.knowledgeCheck?.questions,
              },
            })
          }

          setAgendaItems(_agendaItems)
        })
        .catch((err) => {
          console.og('load data error :: err', err)
        })
        .finally(() => {
          setIsDataLoading(false)
        })
    }
  }, [
    getAgendaItemsPreparation,
    getAgendaItemsDay1,
    getAgendaItemsDay2,
    getAgendaItemsDay3,
    getAgendaItemsDay4,
    isEditOn,
    isManager,
    isFacilitator,
    isParticipant,
    navigate,
    page,
  ])

  // Load data every time the page is change
  useEffect(() => {
    if (page !== '') {
      loadData()
    }
  }, [loadData, page])

  // Reload data when publish is successful
  useEffect(() => {
    if (!isEditOn) {
      return
    }

    if (editStatus.published === true) {
      loadData()
    }
  }, [editStatus.published, loadData, isEditOn])

  const handleOpenEditor = () => {
    // Apply the updated data in editStatus to updatedModules so that AG Grid
    // has the most up-to-date data
    if (Array.isArray(getEditStatus().changes)) {
      let _updatedModules = []

      for (let i = 0; i < getEditStatus().changes.length; i++) {
        const change = getEditStatus().changes[i]
        const page = change.page
        _updatedModules = applyUpdatedData(
          _updatedModules,
          page,
          change,
          getEditStatus().originalData?.modules ?? []
        )
      }

      setUpdatedModules(_updatedModules)
    }

    // Open up the editor dialog
    setSearchParams({
      ...serializeFromQuery(searchParams),
      editor: 'true',
      reorder: 'true',
    })
  }

  const handleEditorCancel = () => {
    const { editor, reorder, ...rest } = serializeFromQuery(searchParams)
    setSearchParams(rest)
  }

  const handleEditorConfirm = (updatedRowData = []) => {
    // Update the updatedModules state
    setUpdatedModules(updatedRowData)

    // Save the changes for publishing
    const newChange = {
      page: 'AGENDA_ITEM',
      type: 'detailModules',
      databaseId: agendaItem?.databaseId,
      data: updatedRowData,
      action: 'reorder',
    }
    const index = getEditStatus().changes.findIndex(
      (change) =>
        change.page === newChange.page &&
        change.type === newChange.type &&
        change.action === newChange.action
    )
    let updatedChanges = Array.from(getEditStatus().changes)

    if (index > -1) {
      updatedChanges[index] = { ...newChange }
    } else {
      updatedChanges.push({ ...newChange })
    }

    // Make sure to remove all `create`, `remove`, and `update` changes
    // related to module.
    // These changes had already been consolidated and updated to reorder data.
    updatedChanges = updatedChanges.filter((change) => {
      if (change.type === 'detailModules') {
        // Keep only `reorder` action
        return change.action === 'reorder'
      }

      // moduleTitle and moduleOverview update type can be excluded
      return !['moduleTitle', 'moduleOverview'].includes(change.type)
    })

    setEditStatus({ ...getEditStatus(), changes: updatedChanges })

    // Close the dialog
    handleEditorCancel()
  }

  /**
   * Add a new module item below item at `index` position
   *
   * @param {[*]} items - An array of module items
   * @param {number} index - Index of item above which the new item to be added
   */
  const handleAddRow = (items, index) => {
    let newRows = Array.from(items)
    const newRowOrder = index + 1
    const newRow = generateDummyRow()

    // Add a new row with dummy data below the item at `index` position
    newRows.splice(newRowOrder, 0, newRow)

    // Add this to the editStatus
    const newChange = {
      page: 'AGENDA_ITEM',
      type: 'detailModules',
      databaseId: agendaItem?.databaseId,
      id: newRow.id,
      atIndex: newRowOrder,
      data: newRow,
      action: 'create',
    }

    const updatedChanges = Array.from(getEditStatus().changes)
    const changesetIndex = updatedChanges.findIndex(
      (change) =>
        change.page === newChange.page && change.type === newChange.type
    )

    // Determine if this change is related to other (unpublished) changeset
    if (
      changesetIndex > -1 &&
      updatedChanges[changesetIndex].action === 'reorder'
    ) {
      // `reorder` related item
      // Adding item can be added to the `data` field
      updatedChanges[changesetIndex].data.splice(newChange.atIndex, 0, {
        ...newChange.data,
      })
    } else {
      updatedChanges.push({ ...newChange })
    }

    setChangesEditStatus({
      ...getEditStatus(),
      changes: [...updatedChanges],
    })

    // Save the updated modules
    setUpdatedModules([...newRows])
  }

  /**
   * Remove a row at `index`
   *
   * @param {number} index - Index of item to be removed
   */
  const handleRemoveRow = () => {
    if (removingItemId === '') {
      toast.error(`Unable to remove this module: Unknown item.`)
      return
    }

    // Clone the modules array
    const newRows = Array.from(modules)

    // Locate the item
    const itemIndex = newRows.findIndex(
      (newRow) => newRow.id === removingItemId
    )

    if (itemIndex === -1) {
      toast.error(`Unable to remove this module because it no longer exists.`)
      return
    }

    // Remove the module from the list
    newRows.splice(itemIndex, 1)

    // Add this to the editStatus
    const newChange = {
      databaseId: agendaItem?.databaseId,
      id: removingItemId,
      page: 'AGENDA_ITEM',
      type: 'detailModules',
      atIndex: itemIndex,
      action: 'remove',
    }

    const updatedChanges = Array.from(getEditStatus().changes)
    const index = updatedChanges.findIndex(
      (change) =>
        change.page === newChange.page && change.type === newChange.type
    )

    // Determine if this change is related to other (unpublished) changeset
    if (index > -1) {
      // `reorder` related item
      if (updatedChanges[index].action === 'reorder') {
        // Removing item is in the `data` field
        const removingIndex = updatedChanges[index].data.findIndex(
          (d) => d.id === newChange.id
        )

        if (removingIndex > -1) {
          updatedChanges[index].data.splice(removingIndex, 1)
        }
      }
      // Newly `create` related (unpublished) item
      else if (
        updatedChanges[index].action === 'create' &&
        updatedChanges[index].id === newChange.id
      ) {
        updatedChanges.splice(index, 1)
      }
      // `update`ed related (unpublished) item
      else if (
        updatedChanges[index].action === 'update' &&
        updatedChanges[index].id === newChange.id
      ) {
        // We can safely remove the item without executing the unpublished updates.
        updatedChanges[index] = { ...newChange }
      } else {
        updatedChanges.push({ ...newChange })
      }
    } else {
      updatedChanges.push({ ...newChange })
    }

    setChangesEditStatus({ ...getEditStatus(), changes: updatedChanges })

    // Save the updated modules
    setUpdatedModules(newRows)

    // Close the confirm dialog
    setRemovingItemId('')
  }

  const handleOpenAgendaModuleEditor = (moduleId = '') => {
    // Open up the editor dialog
    setSearchParams({
      ...serializeFromQuery(searchParams),
      editor: 'true',
      module: 'true',
      id: moduleId,
    })
  }
  const handleAgendaModuleEditorCancel = () => {
    const { editor, module, id, ...rest } = serializeFromQuery(searchParams)
    setSearchParams(rest)
  }
  const handleAgendaModuleEditorConfirm = (updatedData = {}) => {
    if (!updatedData) {
      toast.error(
        `An error has occurred which prevents your changes to materials from being saved properly.`
      )
      return
    }

    // Update the updatedModules state
    const _updatedModules = modules.map((module) => {
      if (module.id === updatedData.id) {
        return updatedData
      } else {
        return module
      }
    })

    const isChanged = !isEqual(
      removeTypeName(agendaItem?.detail?.modules),
      _updatedModules
    )

    if (isChanged) {
      setUpdatedModules(_updatedModules)
    }

    // Save the changes for publishing
    const newChange = {
      page: 'AGENDA_ITEM',
      type: 'detailModules',
      databaseId: agendaItem?.databaseId,
      id: updatedData.id,
      data: updatedData,
      action: 'update',
    }

    const updatedChanges = Array.from(getEditStatus().changes)
    const index = updatedChanges.findIndex(
      (change) =>
        change.page === newChange.page &&
        change.type === newChange.type &&
        change.id === newChange.id
    )

    if (index > -1) {
      // Newly created item can receive updates in the same changeset
      if (updatedChanges[index].action === 'create') {
        newChange.action = 'create'
        newChange.atIndex = updatedChanges[index].atIndex
      }

      if (isChanged) {
        updatedChanges[index] = { ...newChange }
      } else {
        updatedChanges.splice(index, 1)
      }
    } else {
      if (isChanged) {
        updatedChanges.push({ ...newChange })
      }
    }

    setChangesEditStatus({ ...getEditStatus(), changes: updatedChanges })

    // Close the dialog
    handleAgendaModuleEditorCancel()
  }

  if (isDataLoading) {
    return <LoadingSkeleton />
  } else if (!agendaItem) {
    return null
  } else {
    return (
      <>
        {!moduleLock && (
          <>
            {isEditOn && (
              <>
                <ConfirmDialog
                  open={removingItemId !== ''}
                  title="Remove Module"
                  text="Are you sure you want to remove this module?"
                  onCancel={() => setRemovingItemId('')}
                  onConfirm={handleRemoveRow}
                />
                <DialogReorderAgendaItems
                  open={
                    searchParams.get('editor') === 'true' &&
                    searchParams.get('reorder') === 'true'
                  }
                  rowData={modules ?? []}
                  setRowData={setUpdatedModules}
                  title="Reorder Modules"
                  yesText="Save"
                  noText="Cancel"
                  onConfirm={handleEditorConfirm}
                  onCancel={handleEditorCancel}
                />
                {/** Agenda item's modules editor */}
                <DialogEditAgendaModuleItem
                  open={
                    searchParams.get('editor') === 'true' &&
                    searchParams.get('module') === 'true' &&
                    searchParams.get('id') !== ''
                  }
                  rowData={modules ?? []}
                  title="Agenda Module Editor"
                  yesText="Save"
                  noText="Cancel"
                  onConfirm={handleAgendaModuleEditorConfirm}
                  onCancel={handleAgendaModuleEditorCancel}
                />
              </>
            )}

            {/** Agenda item title */}
            <h2 className="mb-4 font-bold text-skin-accent">
              <div className="flex">
                <div
                  dangerouslySetInnerHTML={{
                    __html: agendaItem?.title ?? '<div></div>',
                  }}
                />
                <span>&nbsp;Modules</span>
              </div>
            </h2>

            {/** Agenda item list */}
            {modules?.map((d, index) => {
              return (
                <Disclosure
                  as="div"
                  key={`${index}${d.title}`}
                  className="relative"
                >
                  {({ open }) => (
                    <>
                      {isEditOn && (
                        <div className="absolute -right-[calc(4*14px)] top-1/2 flex -translate-y-1/2 justify-between space-x-1">
                          <button onClick={() => handleAddRow(modules, index)}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={3}
                              stroke="currentColor"
                              className="h-[14px] w-[14px] stroke-green"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 4.5v15m7.5-7.5h-15"
                              />
                            </svg>
                          </button>
                          <button
                            className="disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={index === 0}
                            onClick={() =>
                              index > 0 ? setRemovingItemId(d.id) : null
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                              className="h-[14px] w-[14px] stroke-red-500"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                              />
                            </svg>
                          </button>
                          <button
                            className="disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={modules.length < 2}
                            onClick={
                              modules.length > 1 ? handleOpenEditor : null
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                              className="h-[14px] w-[14px]"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                      <div
                        role={isEditOn ? 'button' : 'none'}
                        className={clsx(
                          isEditOn &&
                            'relative z-10 cursor-pointer outline outline-0 hover:z-20 hover:outline-1 hover:outline-beer active:translate-y-[0.75px] active:scale-[0.99]'
                        )}
                        onClick={
                          isEditOn
                            ? () => handleOpenAgendaModuleEditor(d.id)
                            : () => null
                        }
                      >
                        <Disclosure.Button
                          as={isEditOn ? 'div' : 'button'}
                          disabled={isEditOn}
                          className={clsx(
                            'group flex w-full items-center justify-between px-2 py-2 text-black-olive [&[aria-expanded=true]]:bg-flame [&[aria-expanded=true]]:text-white [&[aria-expanded=true]_svg_polygon]:!fill-white',
                            index % 2 === 0
                              ? 'bg-bright-gray'
                              : 'bg-american-silver',
                            isEditOn && 'select-none hover:bg-beer/25'
                          )}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html:
                                d.title === '<div></div>'
                                  ? '<div>New Module Title</div>'
                                  : d.title,
                            }}
                          />
                          <ChevronRightIcon
                            className={`${
                              open ? 'rotate-90 transform' : ''
                            } w-[6px]`}
                          />
                        </Disclosure.Button>
                        {!isEditOn && (
                          <Disclosure.Panel className="px-3 pb-8 pt-4">
                            {/** Module description */}
                            <div
                              dangerouslySetInnerHTML={{
                                __html: d.overview ?? '<div></div>',
                              }}
                            />

                            {/** Activity Materials */}
                            {d.materials?.length > 0 && (
                              <div className="mb-10 mt-6">
                                <h3
                                  className="mb-2 text-lg font-bold text-skin-accent"
                                  dangerouslySetInnerHTML={{
                                    __html: d.materialsTitle ?? '<div></div>',
                                  }}
                                />
                                <div className="mt-4 [&>a:not(:last-of-type)]:mb-3">
                                  {d.materials.map((material, materialIndex) =>
                                    isEditOn ? (
                                      <div
                                        key={`${material.id ?? materialIndex}`}
                                        className={
                                          'flex justify-between rounded-[5.5px] bg-bright-gray px-4 py-3 text-xs text-black-olive [&:not(:last-of-type)]:mb-3'
                                        }
                                      >
                                        <span>{material.name}</span>
                                        <LinkIcon className="w-[20px]" />
                                      </div>
                                    ) : (
                                      <a
                                        key={`${material.id ?? materialIndex}`}
                                        href={material.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={
                                          'flex justify-between rounded-[5.5px] bg-bright-gray px-4 py-3 text-xs text-black-olive active:translate-y-[0.75px] active:scale-[0.99] [&:not(:last-of-type)]:mb-3'
                                        }
                                      >
                                        <span>{material.name}</span>
                                        <LinkIcon className="w-[20px]" />
                                      </a>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </Disclosure.Panel>
                        )}
                      </div>
                    </>
                  )}
                </Disclosure>
              )
            })}
          </>
        )}
        {Array.isArray(agendaItem?.detail?.knowledgeCheck?.questions) &&
          agendaItem?.detail?.knowledgeCheck?.questions.length > 0 && (
            <h2 className="mb-4 mt-14 font-bold text-skin-accent">
              {agendaItem?.title ?? '--'} Reflection Point
            </h2>
          )}
        {kcLock ? (
          <div className="flex flex-col items-center justify-center rounded-[8px] bg-bright-gray px-20 py-8">
            <LockIcon className="h-[68px] w-[57px]" />
            <p className="mt-4 text-center">
              Return here at the end of the day to answer questions and earn
              points on the leaderboard!
            </p>
          </div>
        ) : (
          <KnowledgeCheck
            databaseId={agendaItem?.databaseId}
            postType="AGENDA_ITEM"
            postId={agendaItem?.postId}
            questions={agendaItem?.detail?.knowledgeCheck?.questions ?? []}
          />
        )}
      </>
    )
  }
}

const generateDummyRow = () => {
  const dummyRow = {
    id: uuidv4(),
    // title: '<p>New Module Title</p>',
    title: '<div></div>',
    // overview: '<p>Enter overview here...</p>',
    overview: '<p></p>',
    materialsTitle: '<div></div>',
    materials: [
      // {
      //   id: uuidv4(),
      //   name: 'Click to download activity material',
      //   url: 'https://example.com',
      // },
    ],
  }

  return dummyRow
}

const LoadingSkeleton = () => {
  return (
    <div role="status" className="animate-pulse pt-6">
      {/** Module title */}
      <div className="mb-8 flex w-full items-center space-x-2">
        <div className="h-2.5 w-1/3 rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      {/** Module dropdown items */}
      <div className="flex w-full items-center space-x-2 pt-2">
        <div className="w-full bg-gray-200 py-5 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full items-center space-x-2">
        <div className="w-full bg-gray-300 py-5 dark:bg-gray-700"></div>
      </div>
      <div className="flex w-full items-center space-x-2">
        <div className="w-full bg-gray-200 py-5 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full items-center space-x-2">
        <div className="w-full bg-gray-300 py-5 dark:bg-gray-700"></div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export default AgendaDetail
